/// <reference types="google.maps" />
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, MapPin, Crosshair } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { PLACE_CATEGORIES, PlaceCategory, useAddPlace, useNearbyPlacesForDedup } from '@/hooks/usePetPlaces';
import { useGoogleMapsLoader } from '@/hooks/useGoogleMapsLoader';
import { useGeolocation } from '@/hooks/useGeolocation';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userLat: number | null;
  userLng: number | null;
}

const schema = z.object({
  name: z.string().trim().min(2).max(120),
  category: z.enum(PLACE_CATEGORIES as unknown as [string, ...string[]]),
  pet_policy_summary: z.string().trim().max(500).optional(),
  address: z.string().trim().max(200).optional(),
});

export default function AddPlaceDialog({ open, onOpenChange, userLat, userLng }: Props) {
  const { t } = useTranslation();
  const [step, setStep] = useState<'dedup' | 'form'>('dedup');
  const [name, setName] = useState('');
  const [category, setCategory] = useState<PlaceCategory>('dog_park');
  const [summary, setSummary] = useState('');
  const [address, setAddress] = useState('');

  // Pin coordinates (initialized from props, updated by map drag)
  const [pinLat, setPinLat] = useState<number | null>(userLat);
  const [pinLng, setPinLng] = useState<number | null>(userLng);

  // Map refs
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const idleListenerRef = useRef<google.maps.MapsEventListener | null>(null);

  const { isLoaded: mapsLoaded, error: mapsError } = useGoogleMapsLoader();
  const { detectLocation, isDetecting } = useGeolocation();

  const { data: nearby, isLoading: dedupLoading } = useNearbyPlacesForDedup(
    open ? (pinLat ?? userLat) : null,
    open ? (pinLng ?? userLng) : null,
    50
  );
  const { mutateAsync, isPending } = useAddPlace();

  // Reset state on open
  useEffect(() => {
    if (open) {
      setStep('dedup');
      setName('');
      setCategory('dog_park');
      setSummary('');
      setAddress('');
      setPinLat(userLat);
      setPinLng(userLng);
    }
  }, [open, userLat, userLng]);

  // Fallback GPS detection if we don't have coords when entering form step
  useEffect(() => {
    if (!open || step !== 'form') return;
    if (pinLat != null && pinLng != null) return;
    (async () => {
      const res = await detectLocation();
      if (res) {
        setPinLat(res.latitude);
        setPinLng(res.longitude);
      } else {
        // Default fallback: world center-ish so map still renders
        setPinLat(0);
        setPinLng(0);
      }
    })();
  }, [open, step, pinLat, pinLng, detectLocation]);

  // Initialize / refresh map when entering form step
  useEffect(() => {
    if (!open || step !== 'form' || !mapsLoaded) return;
    if (pinLat == null || pinLng == null) return;
    if (!mapContainerRef.current) return;

    if (!mapRef.current) {
      mapRef.current = new google.maps.Map(mapContainerRef.current, {
        center: { lat: pinLat, lng: pinLng },
        zoom: 16,
        disableDefaultUI: true,
        zoomControl: true,
        gestureHandling: 'greedy',
        clickableIcons: false,
      });
      idleListenerRef.current = mapRef.current.addListener('idle', () => {
        const c = mapRef.current?.getCenter();
        if (c) {
          setPinLat(c.lat());
          setPinLng(c.lng());
        }
      });
    }
    return () => {
      // Keep map across re-renders; only cleanup on close handled below
    };
  }, [open, step, mapsLoaded, pinLat, pinLng]);

  // Tear down map when dialog closes
  useEffect(() => {
    if (!open && mapRef.current) {
      idleListenerRef.current?.remove();
      idleListenerRef.current = null;
      mapRef.current = null;
    }
  }, [open]);

  const handleRecenterGps = async () => {
    const res = await detectLocation();
    if (res && mapRef.current) {
      mapRef.current.panTo({ lat: res.latitude, lng: res.longitude });
      setPinLat(res.latitude);
      setPinLng(res.longitude);
    } else if (!res) {
      toast({
        title: t('places.errors.gpsRequired', 'Location required'),
        variant: 'destructive',
      });
    }
  };

  const handleSubmit = async () => {
    if (pinLat == null || pinLng == null) {
      toast({ title: t('places.errors.gpsRequired', 'Location required'), variant: 'destructive' });
      return;
    }
    const parsed = schema.safeParse({ name, category, pet_policy_summary: summary, address });
    if (!parsed.success) {
      toast({
        title: t('places.errors.invalidForm', 'Please check the form'),
        description: parsed.error.issues[0]?.message,
        variant: 'destructive',
      });
      return;
    }
    try {
      await mutateAsync({
        name: parsed.data.name,
        category: parsed.data.category as PlaceCategory,
        pet_policy_summary: parsed.data.pet_policy_summary,
        address: parsed.data.address,
        latitude: pinLat,
        longitude: pinLng,
      });
      toast({
        title: t('places.added.title', 'Thanks for your contribution!'),
        description: t('places.added.desc', 'Your place is now visible to the community.'),
      });
      onOpenChange(false);
    } catch (e: any) {
      toast({ title: t('common.error', 'Error'), description: e.message, variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('places.add.title', 'Add a pet-friendly place')}</DialogTitle>
          <DialogDescription>
            {step === 'dedup'
              ? t('places.add.dedupDesc', 'Is your place already in our list?')
              : t('places.add.formDesc', 'Just a few details, no account validation needed.')}
          </DialogDescription>
        </DialogHeader>

        {step === 'dedup' ? (
          <div className="space-y-3">
            {dedupLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : nearby && nearby.length > 0 ? (
              <div className="space-y-2">
                {nearby.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between border rounded-lg p-3 bg-muted/30"
                  >
                    <div>
                      <div className="font-medium text-sm">{p.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {p.category} · {Math.round(p.distance_m)} m
                      </div>
                    </div>
                    <Badge variant="secondary" className="text-[10px]">
                      <MapPin className="h-3 w-3 mr-1" /> {t('places.nearby', 'Nearby')}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground py-4 text-center">
                {t('places.add.noneNearby', 'No places found near you yet.')}
              </div>
            )}
            <Button className="w-full" onClick={() => setStep('form')}>
              {t('places.add.newPlace', "It's a new place")}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Mini interactive map with draggable center pin */}
            <div>
              <p className="text-xs text-muted-foreground mb-1.5">
                {t(
                  'places.add.dragHint',
                  'Move the map to position the marker on the exact spot'
                )}
              </p>
              <div className="relative rounded-lg overflow-hidden border bg-muted h-56">
                {mapsError ? (
                  <div className="absolute inset-0 flex items-center justify-center text-xs text-destructive px-4 text-center">
                    {mapsError}
                  </div>
                ) : !mapsLoaded || pinLat == null || pinLng == null ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : null}
                <div ref={mapContainerRef} className="absolute inset-0" />
                {/* Fixed center pin overlay */}
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="relative -translate-y-3">
                    <MapPin
                      className="h-9 w-9 text-primary drop-shadow-lg"
                      strokeWidth={2.5}
                      fill="hsl(var(--primary))"
                    />
                  </div>
                </div>
                {/* Recenter GPS button */}
                <button
                  type="button"
                  onClick={handleRecenterGps}
                  disabled={isDetecting}
                  className="absolute bottom-2 right-2 bg-background/95 hover:bg-background border rounded-full p-2 shadow-md transition-colors disabled:opacity-50"
                  aria-label={t('places.add.recenterGps', 'Center on my location')}
                  title={t('places.add.recenterGps', 'Center on my location')}
                >
                  {isDetecting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Crosshair className="h-4 w-4" />
                  )}
                </button>
              </div>
              {pinLat != null && pinLng != null && (
                <div className="flex items-center text-[11px] text-muted-foreground gap-1 mt-1">
                  <MapPin className="h-3 w-3" />
                  {pinLat.toFixed(5)}, {pinLng.toFixed(5)}
                </div>
              )}
            </div>

            <div>
              <Label>{t('places.fields.name', 'Name')} *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
            </div>
            <div>
              <Label>{t('places.fields.category', 'Category')} *</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {PLACE_CATEGORIES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCategory(c)}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                      category === c
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background border-border hover:bg-muted'
                    }`}
                  >
                    {t(`places.categories.${c}`, c)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>{t('places.fields.addressRef', 'Address reference (optional)')}</Label>
              <Input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                maxLength={200}
                placeholder={t(
                  'places.fields.addressRefPlaceholder',
                  'Suite #, floor, landmark…'
                )}
              />
            </div>
            <div>
              <Label>{t('places.fields.summary', 'Pet policy summary')}</Label>
              <Textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                maxLength={500}
                placeholder={t(
                  'places.fields.summaryPlaceholder',
                  'e.g. Small dogs allowed on terrace, water bowls available...'
                )}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setStep('dedup')}>
                {t('common.back', 'Back')}
              </Button>
              <Button className="flex-1" onClick={handleSubmit} disabled={isPending}>
                {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t('places.add.submit', 'Add place')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
